import React, { useEffect, useState } from 'react';
import { Container, Card, Row, Col, Form, Button, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { es } from 'date-fns/locale/es';
import { apiService, Boleta } from '../services/api';
import { printService } from '../services/printService';
import panaApi from '../services/panaApi';

registerLocale('es', es);

function todayStr() {
  const d = new Date();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
}

const Reportes: React.FC = () => {
  const [inicio, setInicio] = useState<Date>(new Date());
  const [fin, setFin] = useState<Date>(new Date());
  const [boletas, setBoletas] = useState<Boleta[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, cantidad: 0, promedio: 0 });

  // Estados para ventas por caja
  const [idCaja, setIdCaja] = useState<number>(1);
  const [fechaCaja, setFechaCaja] = useState<Date>(new Date());
  const [ventasCaja, setVentasCaja] = useState<number | null>(null);
  const [ventasPanaCaja, setVentasPanaCaja] = useState<number>(0);
  const [loadingCaja, setLoadingCaja] = useState(false);

  // Estados para reporte pana
  const [fechaPana, setFechaPana] = useState<Date>(new Date());
  const [totalPana, setTotalPana] = useState<number | null>(null);
  const [loadingPana, setLoadingPana] = useState(false);
  const [pagosPana, setPagosPana] = useState<any[]>([]);

  // Helpers
  const toApiDate = (d: Date | null) => {
    if (!d) return todayStr();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${d.getFullYear()}-${m}-${dd}`;
  };

  const formatFechaDate = (d: Date | null) => {
    if (!d) return '';
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');
    return `${dd}-${m}-${d.getFullYear()}`;
  };

  const consultarVentasCaja = async () => {
    setLoadingCaja(true);
    setVentasCaja(null);
    setVentasPanaCaja(0);
    try {
      const resp = await apiService.getVentasCajaHoy(idCaja, toApiDate(fechaCaja));
      if (resp.ok) {
        setVentasCaja(resp.total_ventas);
        
        // Consultar Pana para la misma fecha de la caja
        try {
          const respPana = await panaApi.getPagosPos(toApiDate(fechaCaja));
          if (respPana.ok) {
            let sumPana = 0;
            if (typeof respPana.total === 'number') {
              sumPana = respPana.total;
            } else if (Array.isArray(respPana.pagos)) {
              sumPana = respPana.pagos.reduce((acc: number, p: any) => acc + (p.total || p.monto || p.subtotal || 0), 0);
            }
            setVentasPanaCaja(sumPana);
          }
        } catch (err) {
          console.warn('No se pudo obtener pagos pana para Z de caja', err);
        }

      } else {
        alert(resp.message || 'Error al obtener ventas de caja');
      }
    } catch (e) {
      console.error(e);
      alert('Error de conexión al consultar caja');
    } finally {
      setLoadingCaja(false);
    }
  };

  const imprimirReporteZ = async () => {
    if (ventasCaja === null) return;
    
    try {
      const resp = await printService.printZReceipt({
        caja: idCaja,
        fecha: formatFechaDate(fechaCaja),
        total: ventasCaja,
        totalPana: ventasPanaCaja
      });
      
      if (resp.success) {
        Swal.fire({ icon: 'success', title: 'Impresión Z', text: 'Se ha abierto el cuadro de impresión del navegador', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'warning', title: 'Impresión Z', text: resp.message || 'La impresión solo está disponible en la app de escritorio' });
      }
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'Error de impresión', text: e.message });
    }
  };

  const consultarPagosPana = async () => {
    setLoadingPana(true);
    setTotalPana(null);
    let user = JSON.parse(localStorage.getItem('user') || '{}');
    let id_usuario = user.id_usuario || '';
       try {
      const resp = await panaApi.getPagosPos(toApiDate(fechaPana), id_usuario);
      console.log(resp);
      
      if (resp.success) {
        let sum = 0;
        if (typeof resp.total_recaudado === 'number') {
          sum = resp.total_recaudado;
        } else if (Array.isArray(resp.pagos)) {
          sum = resp.pagos.reduce((acc: number, p: any) => acc + (p.monto), 0);
        }
        setTotalPana(sum);
        setPagosPana(resp.pagos || []);
      } else {
        alert(resp.message || 'Error al obtener pagos de Pana');
      }
    } catch (e) {
      console.error(e);
      alert('Error de conexión al consultar pagos Pana');
    } finally {
      setLoadingPana(false);
    }
  };

  const imprimirReportePana = async () => {
    if (totalPana === null) return;
    try {
      const resp = await printService.printPanaReport({
        fecha: formatFechaDate(fechaPana),
        total: totalPana,
        pagos: pagosPana
      });
      if (resp.success) {
        Swal.fire({ icon: 'success', title: 'Impresión Pana', text: 'Se ha abierto el cuadro de impresión', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'warning', title: 'Impresión Pana', text: resp.message });
      }
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'Error de impresión', text: e.message });
    }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const resp = await apiService.getBoletas({ fecha_inicio: toApiDate(inicio), fecha_fin: toApiDate(fin), per_page: 200 });
      const data = (resp.estado === 1 && resp.registros) ? resp.registros : [];
      setBoletas(data);
      const total = data.reduce((s, b) => s + (b.total_boleta || 0), 0);
      const cantidad = data.length;
      const promedio = cantidad ? total / cantidad : 0;
      setStats({ total, cantidad, promedio });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const fmt = (v: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v);

  return (
    <Container fluid>
      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i> Reportes</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={(e) => { e.preventDefault(); cargar(); }}>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Label>Fecha inicio</Form.Label>
                <DatePicker 
                  selected={inicio} 
                  onChange={(date: Date | null) => date && setInicio(date)} 
                  dateFormat="dd-MM-yyyy"
                  locale="es"
                  className="form-control"
                />
              </Col>
              <Col md={3}>
                <Form.Label>Fecha fin</Form.Label>
                <DatePicker 
                  selected={fin} 
                  onChange={(date: Date | null) => date && setFin(date)} 
                  dateFormat="dd-MM-yyyy"
                  locale="es"
                  className="form-control"
                />
              </Col>
              <Col md={3}>
                <Button type="submit" variant="primary" disabled={loading}>
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
                  Generar
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Row className="mb-3 g-3">
        <Col md={12}>
          <Card className="shadow-sm border-0 bg-light">
            <Card.Body>
              <h6 className="mb-3 text-muted"><i className="fas fa-cash-register me-2"></i>Venta del Día por Caja</h6>
              <Form onSubmit={(e) => { e.preventDefault(); consultarVentasCaja(); }}>
                <Row className="g-2 align-items-end">
                  <Col md={3}>
                    <Form.Label>Número de Caja</Form.Label>
                    <Form.Control type="number" min="1" value={idCaja} onChange={(e) => setIdCaja(parseInt(e.target.value) || 1)} />
                  </Col>
                  <Col md={3}>
                    <Form.Label>Fecha</Form.Label>
                    <DatePicker 
                      selected={fechaCaja} 
                      onChange={(date: Date | null) => date && setFechaCaja(date)} 
                      dateFormat="dd-MM-yyyy"
                      locale="es"
                      className="form-control"
                    />
                  </Col>
                  <Col md={2}>
                    <Button type="submit" variant="success" disabled={loadingCaja}>
                      <i className={`fas fa-search ${loadingCaja ? 'fa-spin' : ''} me-2`}></i>
                      Consultar
                    </Button>
                  </Col>
                  <Col md={4} className="text-end d-flex align-items-center justify-content-end">
                    {ventasCaja !== null && (
                      <>
                        <div className="me-4 text-start">
                          <span className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Total Caja {idCaja} ({formatFechaDate(fechaCaja)}):</span>
                          <span className="fs-4 fw-bold text-success">{fmt(ventasCaja)}</span>
                        </div>
                        <Button variant="secondary" onClick={imprimirReporteZ}>
                          <i className="fas fa-print me-2"></i>Imprimir Z
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3 g-3">
        <Col md={12}>
          <Card className="shadow-sm border-0 bg-light">
            <Card.Body>
              <h6 className="mb-3 text-muted"><i className="fas fa-qrcode me-2"></i>Pagos Pana (Vouchers) del Día</h6>
              <Form onSubmit={(e) => { e.preventDefault(); consultarPagosPana(); }}>
                <Row className="g-2 align-items-end">
                  <Col md={3}>
                    <Form.Label>Fecha</Form.Label>
                    <DatePicker 
                      selected={fechaPana} 
                      onChange={(date: Date | null) => date && setFechaPana(date)} 
                      dateFormat="dd-MM-yyyy"
                      locale="es"
                      className="form-control"
                    />
                  </Col>
                  <Col md={3}>
                    <Button type="submit" variant="info" disabled={loadingPana} className="text-white fw-bold">
                      <i className={`fas fa-search ${loadingPana ? 'fa-spin' : ''} me-2`}></i>
                      Consultar Pana
                    </Button>
                  </Col>
                  <Col md={6} className="text-end d-flex align-items-center justify-content-end">
                    {totalPana !== null && (
                      <>
                        <div className="me-4 text-start">
                          <span className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Total Pana ({formatFechaDate(fechaPana)}):</span>
                          <span className="fs-4 fw-bold text-info">{fmt(totalPana)}</span>
                        </div>
                        <Button variant="secondary" onClick={imprimirReportePana}>
                          <i className="fas fa-print me-2"></i>Imprimir Pana
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3 g-3">
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0"><Card.Body>
            <h6 className="text-muted mb-1">Total Ventas</h6>
            <h3 className="fw-bold text-primary mb-0">{fmt(stats.total)}</h3>
          </Card.Body></Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0"><Card.Body>
            <h6 className="text-muted mb-1">Cantidad Boletas</h6>
            <h3 className="fw-bold text-success mb-0">{stats.cantidad}</h3>
          </Card.Body></Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0"><Card.Body>
            <h6 className="text-muted mb-1">Promedio por Venta</h6>
            <h3 className="fw-bold text-info mb-0">{fmt(stats.promedio)}</h3>
          </Card.Body></Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light">
          <h6 className="mb-0">Detalle ({boletas.length})</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light"><tr>
              <th>Boleta #</th><th>Fecha/Hora</th><th>Cliente</th><th className="text-end">Total</th>
            </tr></thead>
            <tbody>
              {boletas.map(b => (
                <tr key={b.id_boleta}>
                  <td>#{b.id_boleta}</td>
                  <td>{new Date(b.fecha_ingreso_boleta).toLocaleString('es-CL')}</td>
                  <td>{b.nombre_cliente_boleta || 'Cliente'}</td>
                  <td className="text-end">{fmt(b.total_boleta || 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Reportes;
